import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { formatINR } from "../lib/api";
import { Trash2, Plus, Minus, ShoppingBag, Pencil } from "lucide-react";

/**
 * Order Review Dialog — final confirm step for Distributor & MR carts.
 *
 * Lets the user:
 *  - See every product, size, unit price, BIG quantity input, and line total in a spacious table.
 *  - Type the quantity directly into a large amber-bordered input (primary affordance).
 *  - Increment / decrement with optional +/- helpers.
 *  - Remove individual lines.
 *  - Edit a flat list of address fields right inside the dialog.
 *  - Hit "Add more items" → closes the dialog so they can keep shopping.
 *  - Hit "Confirm & place order" → submits via the `onConfirm` callback.
 */
export default function OrderReviewDialog({
    open,
    onOpenChange,
    cart,
    setQty,
    updateQty,
    removeItem,
    address,
    setAddress,
    addressFields,           // [['name','Customer name *'], ...]
    addressNote,             // string note shown above the address fields
    submitting,
    onConfirm,
    customerLabel,           // optional eyebrow (e.g. "On behalf of: Dr. Roy")
    headerSlot,              // optional React node rendered between KPI strip and items table
    addressHeaderSlot,       // optional React node rendered inside the Shipping-address card, above the field grid
    testIdPrefix = "review", // 'dist' | 'mr' so testids don't collide
}) {
    const subtotal = cart.reduce((s, x) => s + x.price * x.quantity, 0);
    const totalUnits = cart.reduce((s, x) => s + x.quantity, 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-none max-w-5xl max-h-[92vh] overflow-y-auto" data-testid={`${testIdPrefix}-order-review-dialog`}>
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl text-slate-900">Review your order</DialogTitle>
                    <DialogDescription>
                        Final check before placing the order. You can change quantities, remove items, or close to add more.
                    </DialogDescription>
                </DialogHeader>

                {customerLabel && (
                    <div className="bg-sky-50/60 border border-sky-100 px-3 py-2 -mt-2">
                        <span className="text-[10px] uppercase tracking-widest text-stone-500">{customerLabel}</span>
                    </div>
                )}

                {cart.length === 0 ? (
                    <div className="py-12 text-center text-stone-500">
                        Your cart is empty. Close this dialog to add items from the catalog.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* KPI strip */}
                        <div className="grid grid-cols-3 bg-stone-50 border border-stone-200">
                            <KPI label="Items" value={cart.length} />
                            <KPI label="Total units" value={totalUnits} />
                            <KPI label="Order total" value={formatINR(subtotal)} cls="text-sky-700" big />
                        </div>

                        {headerSlot}

                        {/* Items table — spacious, every cell editable */}
                        <div className="border border-stone-200 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-sky-50 border-b border-sky-100">
                                        <Th>Product</Th>
                                        <Th>Size</Th>
                                        <Th cls="text-right">Unit price</Th>
                                        <Th cls="text-center">Quantity</Th>
                                        <Th cls="text-right">Line total</Th>
                                        <Th></Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cart.map((c, i) => (
                                        <tr key={`${c.product_id}-${c.size_id}`} className="border-b border-stone-100" data-testid={`${testIdPrefix}-review-row-${i}`}>
                                            <td className="p-3 align-middle">
                                                <div className="font-medium text-slate-900">{c.name}</div>
                                            </td>
                                            <td className="p-3 align-middle text-stone-600 font-mono text-xs">{c.size_label}</td>
                                            <td className="p-3 align-middle text-right font-mono text-stone-700">{formatINR(c.price)}</td>
                                            <td className="p-3 align-middle">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => updateQty(i, -1)}
                                                        className="w-10 h-12 border border-stone-300 hover:border-sky-700 hover:bg-sky-50 grid place-items-center transition-colors"
                                                        aria-label="Decrease quantity"
                                                        data-testid={`${testIdPrefix}-review-minus-${i}`}
                                                    >
                                                        <Minus className="w-4 h-4 text-stone-600" />
                                                    </button>
                                                    <input
                                                        type="number"
                                                        inputMode="numeric"
                                                        min={1}
                                                        value={c.quantity}
                                                        onChange={(e) => setQty(i, e.target.value)}
                                                        onFocus={(e) => e.target.select()}
                                                        onBlur={(e) => { if (!e.target.value || parseInt(e.target.value, 10) < 1) setQty(i, 1); }}
                                                        className="w-24 h-12 text-center text-xl font-semibold font-mono border-2 border-amber-400 focus:border-sky-700 focus:outline-none bg-amber-50 focus:bg-white"
                                                        data-testid={`${testIdPrefix}-review-qty-${i}`}
                                                        aria-label={`Quantity for ${c.name}`}
                                                    />
                                                    <button
                                                        onClick={() => updateQty(i, 1)}
                                                        className="w-10 h-12 border border-stone-300 hover:border-sky-700 hover:bg-sky-50 grid place-items-center transition-colors"
                                                        aria-label="Increase quantity"
                                                        data-testid={`${testIdPrefix}-review-plus-${i}`}
                                                    >
                                                        <Plus className="w-4 h-4 text-stone-600" />
                                                    </button>
                                                </div>
                                                <div className="text-[10px] uppercase tracking-widest text-stone-400 text-center mt-1">
                                                    <Pencil className="w-2.5 h-2.5 inline mr-0.5" />Type the quantity
                                                </div>
                                            </td>
                                            <td className="p-3 align-middle text-right font-mono font-semibold text-sky-700 text-base" data-testid={`${testIdPrefix}-review-total-${i}`}>
                                                {formatINR(c.price * c.quantity)}
                                            </td>
                                            <td className="p-3 align-middle">
                                                <button
                                                    onClick={() => removeItem(i)}
                                                    className="text-red-700 hover:bg-red-50 border border-red-300 p-1.5 inline-flex items-center"
                                                    aria-label={`Remove ${c.name}`}
                                                    data-testid={`${testIdPrefix}-review-remove-${i}`}
                                                    title="Remove this item"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-sky-700 text-white">
                                        <td colSpan={4} className="p-3 font-semibold uppercase tracking-widest text-xs">Order total</td>
                                        <td className="p-3 text-right font-mono font-bold text-base">{formatINR(subtotal)}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* Address */}
                        {addressFields?.length > 0 && (
                            <div className="border border-stone-200 p-4 space-y-3">
                                <div className="flex items-baseline justify-between">
                                    <h3 className="font-serif text-lg text-slate-900">Shipping address</h3>
                                    {addressNote && <span className="text-[11px] text-stone-500">{addressNote}</span>}
                                </div>
                                {addressHeaderSlot}
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {addressFields.map(([key, label]) => (
                                        <div key={key}>
                                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label>
                                            <Input
                                                value={address[key] || ""}
                                                onChange={(e) => setAddress((a) => ({ ...a, [key]: e.target.value }))}
                                                className="rounded-none border-stone-300 mt-1 h-9"
                                                data-testid={`${testIdPrefix}-review-addr-${key}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter className="border-t border-stone-200 pt-3 flex-wrap gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none border-stone-400" data-testid={`${testIdPrefix}-review-back`}>
                        ← Add more items
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={submitting || cart.length === 0}
                        className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 px-6 py-5"
                        data-testid={`${testIdPrefix}-review-confirm`}
                    >
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        {submitting ? "Placing…" : `Confirm & place order · ${formatINR(subtotal)}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const Th = ({ children, cls = "" }) => (
    <th className={`p-2 text-[10px] uppercase tracking-widest text-stone-500 ${cls || "text-left"}`}>{children}</th>
);

const KPI = ({ label, value, cls = "", big = false }) => (
    <div className="p-3 border-r border-stone-200 last:border-r-0">
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
        <div className={`font-serif ${big ? "text-3xl" : "text-2xl"} ${cls}`}>{value}</div>
    </div>
);
